/**
 * Resource Encoding Error Detection (v2)
 *
 * Passively detects same-origin resources that were transferred from the
 * server but failed to decode (e.g., ERR_CONTENT_DECODING_FAILED from
 * broken Brotli/gzip encoding served by R2 or CDN).
 *
 * Detection heuristic (PerformanceResourceTiming):
 *   transferSize > 0              → data was sent over the network
 *   encodedBodySize > 0           → server included a response body
 *   decodedBodySize === 0         → browser couldn't decompress the body
 *   responseStatus === 200 or -1  → not a 304/redirect/error
 *   transferSize >= encodedBodySize → not a 304 revalidation quirk
 *   encodedBodySize >= 256        → not a trivially small response
 *
 * Verification: re-fetches one suspected resource to confirm. If the
 * re-fetch succeeds, reports as warning (likely false positive from Chrome
 * reporting decodedBodySize=0 for modulepreload resources). If the re-fetch
 * fails, reports as error (confirmed encoding failure).
 *
 * Limitations:
 * - Only detects same-origin resources (cross-origin have zeroed timing data)
 * - SW importScripts failures are in the SW context and not visible here
 *   (those are caught by PwaUpdateBanner's 3s activation timeout instead)
 * - Only checks resources loaded before the idle callback fires; dynamically
 *   imported chunks that fail later are caught by Nuxt's chunk error handler
 */
export default defineNuxtPlugin((nuxtApp) => {
  onNuxtReady(() => {
    const run = () => {
      checkForEncodingErrors(nuxtApp).catch((err) => {
        console.info('[Encoding Check] Detection failed:', err)
      })
    }
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run, { timeout: 10_000 })
    } else {
      setTimeout(run, 5_000)
    }
  })
})

function toRelativePath(url: string, origin: string): string {
  return url.replace(origin, '')
}

/** Minimum encoded body size to consider — smaller responses are likely metrics quirks */
const MIN_ENCODED_BODY_SIZE = 256

/** Max resources to include in a single Sentry report to avoid oversized payloads */
const MAX_RESOURCES_PER_REPORT = 10

interface EncodingErrorEntry {
  url: string
  transferSize: number
  encodedBodySize: number
  decodedBodySize: number
  responseStatus: number
  initiatorType: string
  duration: number
}

function getResponseStatus(entry: PerformanceResourceTiming): number {
  return 'responseStatus' in entry
    ? (entry as { responseStatus: number }).responseStatus
    : -1
}

function isSuspectedEncodingError(
  entry: PerformanceResourceTiming,
  origin: string,
): boolean {
  if (!entry.name.startsWith(origin)) return false

  if (entry.transferSize <= 0) return false
  if (entry.encodedBodySize <= 0) return false
  if (entry.decodedBodySize !== 0) return false

  // Only flag HTTP 200 or unknown (-1) responses — skip 304, redirects, errors
  const status = getResponseStatus(entry)
  if (status !== -1 && status !== 200) return false

  // 304 revalidation quirk: transferSize is just headers (smaller than body)
  if (entry.transferSize < entry.encodedBodySize) return false

  // Very small responses are more likely metrics quirks than real issues
  if (entry.encodedBodySize < MIN_ENCODED_BODY_SIZE) return false

  return true
}

interface RefetchResult {
  ok: boolean
  status: number
  bodySize: number
  headers: Record<string, string>
}

const REFETCH_HEADERS_TO_CAPTURE = [
  'content-encoding',
  'content-type',
  'content-length',
  'cache-control',
  'age',
  'vary',
  'x-backend',
  'cf-ray',
  'cf-cache-status',
]

/**
 * Re-fetch a resource to check if encoding actually works.
 * Returns result details including response headers for diagnostics.
 */
async function refetchResource(url: string): Promise<RefetchResult> {
  try {
    const response = await fetch(url)
    const headers: Record<string, string> = {}
    for (const name of REFETCH_HEADERS_TO_CAPTURE) {
      const value = response.headers.get(name)
      if (value) headers[name] = value
    }
    if (!response.ok) {
      return { ok: false, status: response.status, bodySize: 0, headers }
    }
    const body = await response.arrayBuffer()
    return {
      ok: body.byteLength > 0,
      status: response.status,
      bodySize: body.byteLength,
      headers,
    }
  } catch {
    return { ok: false, status: 0, bodySize: 0, headers: {} }
  }
}

async function checkForEncodingErrors(nuxtApp: { [key: string]: unknown }) {
  if (typeof performance?.getEntriesByType !== 'function') return

  const entries = performance.getEntriesByType(
    'resource',
  ) as PerformanceResourceTiming[]
  const origin = window.location.origin

  const suspects: EncodingErrorEntry[] = []

  for (const entry of entries) {
    if (!isSuspectedEncodingError(entry, origin)) continue

    suspects.push({
      url: entry.name,
      transferSize: entry.transferSize,
      encodedBodySize: entry.encodedBodySize,
      decodedBodySize: entry.decodedBodySize,
      responseStatus: getResponseStatus(entry),
      initiatorType: entry.initiatorType,
      duration: Math.round(entry.duration),
    })
  }

  const firstSuspect = suspects[0]
  if (!firstSuspect) return

  // Re-fetch the first suspect to verify — if it succeeds, this is likely
  // a Chrome PerformanceResourceTiming quirk (e.g., modulepreload reporting
  // decodedBodySize=0 before the module is actually imported/decoded)
  const refetchResult = await refetchResource(firstSuspect.url)
  const refetchPassed = refetchResult.ok
  const confirmed = !refetchPassed
  const level = confirmed ? 'error' : 'warning'

  const relativeUrls = suspects.map((e) => toRelativePath(e.url, origin))

  if (confirmed) {
    console.error(
      `[Encoding Error] ${suspects.length} resource(s) with broken encoding (re-fetch also failed):`,
      relativeUrls,
    )
  } else {
    console.info(
      `[Encoding Check] ${suspects.length} resource(s) reported decodedBodySize=0 but re-fetch succeeded (likely false positive):`,
      relativeUrls,
    )
  }

  const sentry = nuxtApp.$sentry as
    | {
        captureException: (
          error: Error,
          context: Record<string, unknown>,
        ) => void
      }
    | undefined
  if (!sentry) return

  const connection = (
    navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean }
    }
  ).connection

  const error = new Error(
    confirmed
      ? `Resource encoding error: ${suspects.length} resource(s) failed to decode`
      : `Resource encoding warning: ${suspects.length} resource(s) reported decodedBodySize=0 (re-fetch OK)`,
  )
  error.name = confirmed ? 'ResourceEncodingError' : 'ResourceEncodingWarning'

  sentry.captureException(error, {
    level,
    tags: {
      'error-source': 'encoding-error-detection',
      'resource-count': String(suspects.length),
      'refetch-passed': String(refetchPassed),
    },
    extra: {
      diagnostics: {
        visibilityState: document.visibilityState,
        swActive: !!navigator.serviceWorker?.controller,
        effectiveType: connection?.effectiveType ?? 'unknown',
        saveData: connection?.saveData ?? false,
      },
      refetch: {
        url: toRelativePath(firstSuspect.url, origin),
        passed: refetchPassed,
        status: refetchResult.status,
        bodySize: refetchResult.bodySize,
        headers: refetchResult.headers,
      },
      resources: suspects.slice(0, MAX_RESOURCES_PER_REPORT).map((e) => ({
        url: toRelativePath(e.url, origin),
        transferSize: e.transferSize,
        encodedBodySize: e.encodedBodySize,
        decodedBodySize: e.decodedBodySize,
        responseStatus: e.responseStatus,
        initiatorType: e.initiatorType,
        duration: e.duration,
      })),
    },
  })
}
